/** Detect which instrument has a non-zero open position from the Kite
 * positions response. We use this to decide whether an instrument-override
 * click on the strategy detail page needs the warn-modal flow.
 *
 * Heuristic: tradingsymbols for our F&O contracts start with the underlying
 *   name -- e.g. "NIFTY26JUL24000CE" / "SENSEX2670280400PE".
 * Position is considered open when |quantity| > 0. The positions endpoint
 * keeps closed (squared-off) rows around with quantity=0.
 */

import type { KitePosition } from "@/types/broker";
import type { Instrument } from "./instrument";

/** The underlying a contract tradingsymbol belongs to, or null.
 *
 * "NIFTY26SEP22500CE" -> NIFTY, "SENSEX2670280400PE" -> SENSEX. Safe against
 * the other NIFTY-family roots because none of them START with "NIFTY" —
 * BANKNIFTY, FINNIFTY and MIDCPNIFTY all carry a prefix before it, and the
 * bot trades neither. Returns null rather than guessing on anything else. */
export function underlyingOf(tradingsymbol: string | null): Instrument | null {
  if (tradingsymbol === null) return null;
  const sym = tradingsymbol.toUpperCase();
  if (sym.startsWith("NIFTY")) return "NIFTY";
  if (sym.startsWith("SENSEX")) return "SENSEX";
  return null;
}

export function openInstrument(positions: KitePosition[]): Instrument | null {
  for (const p of positions) {
    if (p.quantity === 0) continue;
    const found = underlyingOf(p.tradingsymbol);
    if (found !== null) return found;
  }
  return null;
}
